import { blockRegistry } from './blockRegistry';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import store, { history } from '../../store/store';

const ISLAND_SELECTOR = '.react-island:not([data-hydrated="true"]):not([data-hydrating="true"])';

const HYDRATION_STRATEGIES = {
  immediate: callback => callback(),
  idle: callback => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback);
    } else {
      setTimeout(callback, 200);
    }
  },
  visible: (element, callback) => {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting) {
            observer.disconnect();
            callback();
          }
        },
        { rootMargin: '50px' }
      );
      observer.observe(element);
    } else {
      callback();
    }
  }
};

export function hydrateIsland(island) {
  if (
    island.getAttribute('data-hydrated') === 'true' ||
    island.getAttribute('data-hydrating') === 'true'
  ) {
    return;
  }

  island.setAttribute('data-hydrating', 'true');

  const blockName = (
    island.getAttribute('data-react-block') ||
    island.getAttribute('data-block') ||
    ''
  ).trim();

  const strategy = island.getAttribute('data-hydration-strategy') || 'immediate';

  if (!blockName) {
    island.setAttribute('data-hydrated', 'true');
    island.removeAttribute('data-hydrating');
    return;
  }

  const BlockComponent = blockRegistry[blockName];
  const isValidComponent =
    typeof BlockComponent === 'function' ||
    (typeof BlockComponent === 'object' &&
      BlockComponent !== null &&
      (BlockComponent.$$typeof || BlockComponent.render));

  if (!isValidComponent) {
    console.warn(
      '[blockBinder] Okänd eller ogiltig block-komponent:',
      blockName,
      BlockComponent,
      blockRegistry
    );
    island.setAttribute('data-hydrated', 'true');
    island.removeAttribute('data-hydrating');
    return;
  }

  const hydrate = () => {
    try {
      const encodedProps = island.getAttribute('data-block-props') || '';
      const props = encodedProps ? JSON.parse(decodeURIComponent(encodedProps)) : {};

      const ssrNode = island.querySelector('.react-island__ssr');
      const appNode = island.querySelector('.react-island__app');

      if (!appNode) {
        island.setAttribute('data-hydrated', 'true');
        return;
      }
      ReactDOM.render(
        React.createElement(
          Provider,
          { store },
          React.createElement(Router, { history }, React.createElement(BlockComponent, props))
        ),
        appNode,
        () => {
          island.setAttribute('data-hydrated', 'true');
          island.removeAttribute('data-hydrating');

          if (ssrNode) {
            ssrNode.setAttribute('hidden', true);
            ssrNode.setAttribute('aria-hidden', 'true');
          }
        }
      );
    } catch (e) {
      console.error('[blockBinder] hydrateIsland misslyckades för', blockName, e);
      island.setAttribute('data-hydrated', 'true');
      island.removeAttribute('data-hydrating');
    }
  };

  const runStrategy = HYDRATION_STRATEGIES[strategy] || HYDRATION_STRATEGIES.immediate;

  if (strategy === 'visible') {
    runStrategy(island, hydrate);
  } else {
    runStrategy(hydrate);
  }
}

export function hydrateBlocks(root = document) {
  const islands = root.querySelectorAll(ISLAND_SELECTOR);
  islands.forEach(hydrateIsland);
}

export function observeAndHydrateBlocks() {
  hydrateBlocks(document);

  let scheduled = false;
  const scan = () => {
    scheduled = false;
    hydrateBlocks(document);
  };

  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    (window.requestAnimationFrame || setTimeout)(scan);
  });

  observer.observe(document.body, { childList: true, subtree: true });

  return () => observer.disconnect();
}

export function SSRPlaceholder({ blockId, orderIndex }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!blockId || !ref.current) return;

    const selector = '[data-block-id="' + blockId + '"]';

    const getMatchingIsland = () => {
      let matches;
      try {
        matches = Array.from(document.querySelectorAll(selector));
      } catch (e) {
        console.error('[blockBinder] Ogiltig selector för blockId', blockId, e);
        return null;
      }
      if (typeof orderIndex === 'number') {
        return matches[orderIndex] || null;
      }
      return matches[0] || null;
    };

    const mountIsland = found => {
      if (!ref.current || found.parentNode === ref.current) return;

      const parent = ref.current.parentNode;
      if (parent && typeof orderIndex === 'number') {
        const siblings = Array.from(parent.children);
        const refIndex = siblings.indexOf(ref.current);
        const target = siblings[refIndex] || null;
        parent.insertBefore(found, target);
      } else {
        ref.current.appendChild(found);
      }

      found.style.removeProperty('display');
      found.style.removeProperty('position');
      found.style.removeProperty('clip');
      found.style.removeProperty('clip-path');
      found.removeAttribute('hidden');
      found.removeAttribute('aria-hidden');

      setReady(true);
      hydrateIsland(found);
    };

    const existing = getMatchingIsland();
    if (existing) {
      mountIsland(existing);
      return;
    }

    let timeoutId = null;
    const obs = new MutationObserver(() => {
      const found = getMatchingIsland();
      if (!found) return;
      obs.disconnect();
      clearTimeout(timeoutId);
      mountIsland(found);
    });

    obs.observe(document.body, { childList: true, subtree: true });
    timeoutId = setTimeout(() => obs.disconnect(), 5000);

    return () => {
      clearTimeout(timeoutId);
      obs.disconnect();
    };
  }, [blockId, orderIndex, ready]);

  return <div ref={ref} data-island-placeholder={blockId} />;
}
